'use client'
import { envs } from '@config/envs'
import { hermesLogger } from '@core/observability'
import { useGoogleLogin } from '@react-oauth/google'
import { safeLocalStorage } from '@utils/utilities'
import axios from 'axios'
import { useRouter, useSearchParams } from 'next/navigation'
import { useState } from 'react'

import { AuthHandler, AuthToken } from '../../utils'
import { UseLoginHookInput, UseLoginHookOutput } from './use-login.hook.types'

export default function useLogin(
  props?: UseLoginHookInput
): UseLoginHookOutput {
  const [error, setError] = useState<boolean>(false)
  const router = useRouter()
  const searchParams = useSearchParams()
  const returnUrl = searchParams.get('returnUrl') ?? '/'

  function onError(error: unknown) {
    hermesLogger.error('[@core/auth/hooks] Error to login', { error })
    AuthHandler.clearTokens()
    props?.onError?.()
  }

  async function loginUser(code: string) {
    try {
      safeLocalStorage.clear()

      const { data } = await axios.post<AuthToken>(
        `${envs.cerberusApiUrl}/auth/login`,
        null,
        {
          headers: {
            'auth-code': code,
            'redirect-uri': envs.clientUrl,
          },
        }
      )

      hermesLogger.info('[@core/auth/hooks] Success to login', { data })

      AuthHandler.setTokens(data.accessToken, data.refreshToken)
      setError(false)
      props?.onSuccess?.()

      router.push(returnUrl)
    } catch (error) {
      setError(true)

      hermesLogger.info('[@core/auth/hooks] Error in login', { error })

      onError(error)
    }
  }

  const startGoogleLogin = useGoogleLogin({
    ...props,
    flow: 'auth-code',
    ux_mode: 'popup',
    enable_serial_consent: true,
    onSuccess: async (response) => {
      hermesLogger.info(
        '[@core/auth/hooks] Logged in with google successfully',
        { response }
      )

      await loginUser(response.code)
    },

    onError,
  })

  return {
    startGoogleLogin,
    error,
  }
}
